import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    forwardRef,
    inject,
    Input
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormGroup,
    ValidationErrors,
    Validator
} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ButtonModule} from 'primeng/button';
import {CheckboxModule} from 'primeng/checkbox';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {TextareaModule} from 'primeng/textarea';
import {ToggleSwitchModule} from 'primeng/toggleswitch';
import {
    AutomationPolicyConfig,
    AutomationPolicyPresetSpec,
    AutomationPolicySpec
} from '@webmate-web-core/shared/types/browsersession';
import {
    AutomationPolicyEditorMode,
    AutomationPolicyEditorService
} from '@webmate-web-core/shared/components/browsersession/automation-policy-editor.service';

@Component({
    selector: 'wm-automation-policy-editor',
    standalone: true,
    templateUrl: './automation-policy-editor.component.html',
    styleUrls: ['./automation-policy-editor.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        CheckboxModule,
        DropdownModule,
        InputTextModule,
        TextareaModule,
        ToggleSwitchModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutomationPolicyEditorComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => AutomationPolicyEditorComponent),
            multi: true,
        },
    ],
})
export class AutomationPolicyEditorComponent implements ControlValueAccessor, Validator {
    private readonly destroyRef = inject(DestroyRef);

    @Input() mode: AutomationPolicyEditorMode = 'web';

    readonly targetTypeOptions = [
        {label: 'CSS selector', value: 'selector' as const},
        {label: 'Label text', value: 'label' as const},
        {label: 'name=', value: 'name' as const},
        {label: 'placeholder=', value: 'placeholder' as const},
    ];

    readonly inputModeOptions = [
        {label: 'Set value (native)', value: 'nativeSet' as const},
        {label: 'Type (fast)', value: 'type' as const},
        {label: 'Type (with delay)', value: 'typeWithDelay' as const},
    ];

    readonly submitOptions = [
        {label: 'Do not submit', value: 'none' as const},
        {label: 'Press Enter on last field', value: 'pressEnterOnLastFilled' as const},
        {label: 'Click a submit selector', value: 'clickSelector' as const},
        {label: 'Submit nearest form', value: 'submitNearestFormOfLastFilled' as const},
    ];

    readonly mobileLocatorStrategyOptions = [
        {label: 'Accessibility ID', value: 'AccessibilityId' as const},
        {label: 'Resource ID (Android)', value: 'Id' as const},
        {label: 'Visible Text', value: 'Text' as const},
        {label: 'XPath', value: 'XPath' as const},
    ];

    form: UntypedFormGroup;

    private passthroughPresetSpecs: AutomationPolicyPresetSpec[] = [];
    private passthroughPolicySpecs: AutomationPolicySpec[] = [];
    private onChange: (value: AutomationPolicyConfig | undefined) => void = () => undefined;
    private onTouched: () => void = () => undefined;
    private onValidatorChange: () => void = () => undefined;

    constructor(
        private readonly automationPolicyEditorService: AutomationPolicyEditorService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {
        this.form = this.automationPolicyEditorService.createForm();
        this.bindForm();
    }

    get fields(): UntypedFormArray {
        return this.form.get('fields') as UntypedFormArray;
    }

    get cookies(): UntypedFormArray {
        return this.form.get('cookies') as UntypedFormArray;
    }

    get localStorageItems(): UntypedFormArray {
        return this.form.get('localStorageItems') as UntypedFormArray;
    }

    writeValue(value: AutomationPolicyConfig | undefined): void {
        const parseResult = this.automationPolicyEditorService.parseConfig(value, this.mode);
        this.passthroughPresetSpecs = parseResult.passthroughPresetSpecs;
        this.passthroughPolicySpecs = parseResult.passthroughPolicySpecs;
        this.form = this.automationPolicyEditorService.createForm(parseResult.initialValue);
        this.bindForm();
        this.changeDetectorRef.markForCheck();
    }

    registerOnChange(fn: (value: AutomationPolicyConfig | undefined) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable({emitEvent: false});
        } else {
            this.form.enable({emitEvent: false});
        }

        this.changeDetectorRef.markForCheck();
    }

    validate(_: AbstractControl): ValidationErrors | null {
        return this.form.valid ? null : {automationPolicyInvalid: true};
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    addField(): void {
        this.fields.push(this.automationPolicyEditorService.createFieldGroup());
        this.touch();
    }

    removeField(index: number): void {
        this.fields.removeAt(index);
        this.touch();
    }

    addCookie(): void {
        this.cookies.push(this.automationPolicyEditorService.createCookieGroup());
        this.touch();
    }

    removeCookie(index: number): void {
        this.cookies.removeAt(index);
        this.touch();
    }

    addLocalStorageItem(): void {
        this.localStorageItems.push(this.automationPolicyEditorService.createLocalStorageItemGroup());
        this.touch();
    }

    removeLocalStorageItem(index: number): void {
        this.localStorageItems.removeAt(index);
        this.touch();
    }

    private bindForm(): void {
        this.form.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.emitValue());

        this.form.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.onValidatorChange());

        this.emitValue();
    }

    private emitValue(): void {
        this.onChange(
            this.automationPolicyEditorService.serializeForm(
                this.form,
                this.passthroughPresetSpecs,
                this.passthroughPolicySpecs,
                this.mode
            )
        );
        this.onTouched();
        this.onValidatorChange();
    }

    private touch(): void {
        this.emitValue();
        this.changeDetectorRef.markForCheck();
    }
}
